import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useParseImportFileMutation } from "../../../features/import/importApiSlice";

function UploadAndParse({ sourceType, onParsed }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [parseFile, { isLoading, error }] = useParseImportFileMutation();
  const [parseResult, setParseResult] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = useCallback(async (uploadedFile) => {
    setFile(uploadedFile);
    setParseResult(null);

    // Validate file type
    const fileExtension = uploadedFile.name.split(".").pop().toLowerCase();
    if (sourceType === "csv" && !["csv", "txt"].includes(fileExtension)) {
      return;
    }
    if (sourceType === "json" && fileExtension !== "json") {
      return;
    }

    // Parse the file
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("sourceType", sourceType);

      const result = await parseFile(formData).unwrap();
      setParseResult(result);

      // Auto-proceed after successful parse
      setTimeout(() => {
        onParsed(result.data, uploadedFile);
      }, 1000);
    } catch (err) {
      console.error("Parse error:", err);
    }
  }, [onParsed, parseFile, sourceType]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files[0]);
      }
    },
    [handleFileUpload],
  );

  const acceptedFormats = sourceType === "csv" ? ".csv,.txt" : ".json";
  const fileTypeLabel = sourceType === "csv" ? "CSV / TXT" : "JSON";

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Datei hochladen
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        Laden Sie Ihre {fileTypeLabel}-Datei hoch, um mit dem Import zu beginnen
      </Typography>

      <Paper
        elevation={0}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          mt: 4,
          p: 6,
          border: 2,
          borderStyle: "dashed",
          borderColor: dragActive ? "primary.main" : "divider",
          bgcolor: dragActive ? "action.hover" : "background.paper",
          textAlign: "center",
          transition: "all 0.3s ease",
          cursor: "pointer",
        }}
      >
        <input
          type="file"
          id="file-upload"
          accept={acceptedFormats}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {!file && !isLoading && (
          <>
            <Icon icon="mdi:cloud-upload" width={64} color="primary" />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Datei hier ablegen oder
            </Typography>
            <Button
              variant="contained"
              component="label"
              htmlFor="file-upload"
              startIcon={<Icon icon="mdi:folder-open" />}
              sx={{ mt: 1 }}
            >
              Datei auswählen
            </Button>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 2 }}
              color="text.secondary"
            >
              Unterstützte Formate: {fileTypeLabel}
            </Typography>
          </>
        )}

        {file && !isLoading && !parseResult && (
          <Box>
            <Icon icon="mdi:file-check" width={64} color="success" />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {file.name}
            </Typography>
            <Chip
              label={`${(file.size / 1024).toFixed(2)} KB`}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
        )}

        {isLoading && (
          <Box>
            <CircularProgress size={64} />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Datei wird analysiert...
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Box>
        )}

        {parseResult && (
          <Box>
            <Icon
              icon="mdi:check-circle"
              width={64}
              style={{ color: "#2e7d32" }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{ mt: 2, color: "success.main" }}
            >
              Datei erfolgreich analysiert!
            </Typography>
            <Box
              sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
            >
              <Chip
                icon={<Icon icon="mdi:table-row" />}
                label={`${parseResult.recordCount || 0} Datensätze`}
                color="primary"
              />
              <Chip
                icon={<Icon icon="mdi:table-column" />}
                label={`${parseResult.columnCount || 0} Spalten`}
                color="secondary"
              />
            </Box>
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 2 }}
              color="text.secondary"
            >
              Weiter zum nächsten Schritt...
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Fehler beim Analysieren der Datei:{" "}
            {error?.data?.message || "Unbekannter Fehler"}
          </Typography>
        </Alert>
      )}

      {sourceType === "csv" && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Hinweis für CSV-Dateien:
          </Typography>
          <Typography variant="caption">
            • Stellen Sie sicher, dass die erste Zeile die Spaltennamen enthält
            <br />
            • Unterstützte Trennzeichen: Semikolon (;) und Komma (,)
            <br />• Die Datei sollte UTF-8 kodiert sein (für Umlaute)
          </Typography>
        </Alert>
      )}

      {sourceType === "json" && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Hinweis für JSON-Dateien:
          </Typography>
          <Typography variant="caption">
            • Die Datei muss die erwartete Struktur haben (standorte,
            vertragsnehmer, mitglieder, etc.)
            <br />
            • Das System erkennt automatisch die verschiedenen Entitäten
            <br />• Alle IDs werden beim Import neu zugewiesen
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

UploadAndParse.propTypes = {
  sourceType: PropTypes.string.isRequired,
  onParsed: PropTypes.func.isRequired,
};

export default UploadAndParse;
